import { useState } from "react";
import {
  IconButton,
  InputAdornment,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useTranslation } from "react-i18next";

export type PasswordFieldProps = Omit<TextFieldProps, "type"> & {
  /**
   * Controlled visibility. Omit and the field owns its own state.
   *
   * Pass it to make several fields reveal together — a "new password" and its
   * confirmation are one decision, and revealing half of that pair leaves the
   * confirmation checking a value you still cannot read.
   */
  shown?: boolean;
  /**
   * Omit on a *follower* field in a controlled group: it then tracks `shown`
   * without drawing a second eye button for the same state.
   */
  onToggle?: () => void;
  /** Override when one button governs more than one field (plural copy). */
  showLabel?: string;
  hideLabel?: string;
};

/**
 * A password input with a reveal toggle.
 *
 * Three behaviours, from two optional props:
 *   neither          → self-managed, draws its own button
 *   shown + onToggle → controlled, draws the button (the group's leader)
 *   shown alone      → controlled, no button (a follower)
 */
export function PasswordField({
  shown,
  onToggle,
  showLabel,
  hideLabel,
  ...textFieldProps
}: PasswordFieldProps) {
  const { t } = useTranslation("common");
  const [selfShown, setSelfShown] = useState(false);

  const controlled = shown !== undefined;
  const visible = controlled ? shown : selfShown;
  // A follower is controlled but has no way to toggle: no button for it.
  const toggle = controlled ? onToggle : () => setSelfShown((value) => !value);

  return (
    <TextField
      {...textFieldProps}
      type={visible ? "text" : "password"}
      slotProps={{
        ...textFieldProps.slotProps,
        input: {
          ...textFieldProps.slotProps?.input,
          ...(toggle
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggle}
                      edge="end"
                      aria-label={
                        visible
                          ? (hideLabel ?? t("password.hide"))
                          : (showLabel ?? t("password.show"))
                      }
                    >
                      {visible ? (
                        <VisibilityOffOutlinedIcon />
                      ) : (
                        <VisibilityOutlinedIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : {}),
        },
      }}
    />
  );
}
